import { usePendingImage } from "@ui/hooks/usePendingImage";
import { Carousel } from "@ui/molecules/Carousel/Carousel";
import { Heading } from "@ui/typography/Heading";
import { For, type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import "./BoardCard.css";

export type BoardCardThumbnail = {
	url?: string;
	alt?: string;
	highlighted?: boolean;
};

const STRIP_TILE_COUNT = 4;

export const BoardCard = (props: {
	title?: string;
	/** Shown straight after the title, which still truncates before it. */
	titleSuffix?: JSX.Element;
	href?: string;
	class?: string;
	info?: JSX.Element;
	meta?: JSX.Element;
	thumbnails: BoardCardThumbnail[];
	/** `carousel` scrolls every thumbnail on one row; `strip` shows what fits. */
	layout?: "strip" | "carousel";
}) => {
	const layout = () => props.layout ?? "strip";
	const blankTiles = () =>
		layout() === "strip"
			? new Array(Math.max(0, STRIP_TILE_COUNT - props.thumbnails.length))
			: [];
	const title = () => (
		<Heading as="h3" ellipsis class="board-card-title">
			{props.title}
		</Heading>
	);
	const tiles = () => (
		<>
			<For each={props.thumbnails}>
				{(thumbnail) => {
					// Clip thumbnails in a chat's board artifact can still be generating.
					const image = usePendingImage(() => thumbnail.url);
					return (
						<Show
							when={image.src()}
							fallback={
								<div
									class="board-card-thumbnail board-card-thumbnail-blank"
									classList={{
										"board-card-thumbnail--highlighted": thumbnail.highlighted,
									}}
								/>
							}
						>
							{(url) => (
								<img
									src={url()}
									alt={thumbnail.alt ?? "thumbnail"}
									class="board-card-thumbnail"
									classList={{
										"board-card-thumbnail--highlighted": thumbnail.highlighted,
									}}
								/>
							)}
						</Show>
					);
				}}
			</For>
			<For each={blankTiles()}>
				{() => <div class="board-card-thumbnail-blank" />}
			</For>
		</>
	);

	return (
		<Dynamic
			component={props.href ? "a" : "div"}
			href={props.href}
			class={`board-card ${props.class ?? ""}`}
		>
			<div class="board-card-info">
				<Show when={props.titleSuffix} fallback={title()}>
					<div class="board-card-title-row">
						{title()}
						{props.titleSuffix}
					</div>
				</Show>
				{props.info}
				<div class="board-card-meta">{props.meta}</div>
			</div>
			<div
				class={`board-card-thumbnails board-card-thumbnails--${layout()} ${props.thumbnails.length === 0 ? "board-card-thumbnails--empty" : ""}`}
			>
				<Show when={layout() === "carousel"} fallback={tiles()}>
					<Carousel scrollMode="buttons" scrollType="snap">
						{tiles()}
					</Carousel>
				</Show>
			</div>
		</Dynamic>
	);
};
